// Trial popup: any [data-son-trial="<product id>"] click opens the footer dialog
// immediately. On a guest's page the viewed product's fragment ships with it, so
// that click costs nothing; every other one is fetched with a loader showing. Forms inside
// the fragment POST to the son/v1 routes and swap to the success block; the download
// view keeps one OS panel visible and, for logged-in visitors, swaps to the e-mail
// form and back. Triggers inside the open dialog - a bundle's plug-ins and their
// way back - swap its content and leave it where it sits. A download click reports
// itself and otherwise behaves like the plain link it is. A trigger with
// data-son-trial-mode (the download page) fetches that mode's fragment instead.
// When the fetch fails the click falls through to the button's href.

export const OS_KEY = 'son-trial-os';
const GUEST_KEY = 'son-trial-guest';

function dialog() {
    return document.querySelector('[data-son-trial-dialog]');
}

function content() {
    return dialog()?.querySelector('[data-son-trial-content]');
}

// Each field has its own box under it; the one without a field name takes server errors.
function showError(form, message, field = '') {
    form.querySelectorAll('[data-son-trial-error]').forEach((box) => {
        const own = box.dataset.sonTrialError === field;
        box.textContent = own ? message : '';
        box.hidden = !own || !message;
    });
}

function showSuccess(panel, message) {
    const success = panel.querySelector('[data-son-trial-success]');
    if (!success) {
        return;
    }
    const text = success.querySelector('[data-son-trial-success-text]');
    if (text) {
        text.textContent = message;
    }
    // the form and the way back out of it are done with; only the message stays
    Array.from(panel.children).forEach((el) => {
        el.hidden = el !== success;
    });
}

// A logged-in popup carries the download and the e-mail form at once; the link
// under each is a swap, not a fetch.
function showView(box, name) {
    box.querySelectorAll('[data-son-trial-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.sonTrialPanel !== name;
    });
}

let nonce = null;

// Only a logged-in page names the endpoint: a guest's REST calls need no nonce.
// admin-ajax is never page-cached, so it hands out one for this session.
function restNonce(dlg) {
    const endpoint = dlg.dataset.sonTrialNonceEndpoint;
    if (!endpoint) {
        return Promise.resolve(null);
    }

    nonce ??= fetch(endpoint, { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error('Request failed'))))
        .catch((error) => {
            nonce = null;
            throw error;
        });

    return nonce;
}

/**
 * Every call to son/v1. A failing route answers with a WP_Error, so the HTTP
 * status carries the failure and the body carries the message to show.
 */
async function call(path, options = {}) {
    const dlg = dialog();
    const token = await restNonce(dlg);
    const response = await fetch(`${dlg.dataset.sonTrialEndpoint}${path}`, {
        credentials: 'same-origin',
        headers: {
            ...(token ? { 'X-WP-Nonce': token } : {}),
            ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        },
        ...options,
    });
    // the download route answers 204 with no body at all
    const json = response.status === 204 ? null : await response.json();
    if (!response.ok) {
        throw new Error(json?.message || 'Request failed');
    }

    return json;
}

const fetchFragment = (productId, from, mode) =>
    call(`${mode ? `/${mode}` : ''}/${productId}${from ? `?from=${encodeURIComponent(from)}` : ''}`);

const postForm = (route, fields) =>
    call(`/${route}`, { method: 'POST', body: JSON.stringify(fields) });

export function detectOs() {
    try {
        const stored = window.sessionStorage.getItem(OS_KEY);
        if (stored) {
            return stored;
        }
    } catch (e) {
        // storage blocked
    }

    const platform = navigator.userAgentData?.platform || navigator.platform || '';

    return /mac|iphone|ipad/i.test(platform) ? 'mac' : 'windows';
}

function selectOs(root, os) {
    root.querySelectorAll('[data-son-trial-os]').forEach((tab) => {
        const active = tab.dataset.sonTrialOs === os;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    root.querySelectorAll('[data-son-trial-file]').forEach((panel) => {
        panel.hidden = panel.dataset.sonTrialFile !== os;
    });
    try {
        window.sessionStorage.setItem(OS_KEY, os);
    } catch (e) {
        // storage blocked
    }
}

// A guest's e-mail and terms tick from their last request, for this tab session
// only. Marketing consent is never pre-ticked.
function rememberGuest(form) {
    const email = form.elements.email;
    if (!email || email.readOnly || !form.elements.terms) {
        return;
    }
    try {
        window.sessionStorage.setItem(GUEST_KEY, JSON.stringify({
            email: email.value,
            terms: form.elements.terms.checked,
        }));
    } catch (e) {
        // storage blocked
    }
}

function prefillGuest(root) {
    const form = root.querySelector('[data-son-trial-form="request"]');
    const email = form?.elements.email;
    if (!email || email.readOnly) {
        return;
    }
    let stored = null;
    try {
        stored = JSON.parse(window.sessionStorage.getItem(GUEST_KEY) || 'null');
    } catch (e) {
        // storage blocked
    }
    if (!stored) {
        return;
    }
    if (!email.value) {
        email.value = stored.email || '';
    }
    if (form.elements.terms) {
        form.elements.terms.checked = Boolean(stored.terms);
    }
}

// Every fragment a visitor has opened, so switching between plug-ins costs one
// fetch each and never a second one. A bundle's back-link variant differs from
// the plain one, so the key carries it.
const fragments = new Map();

function fragmentKey(productId, from, mode = '') {
    return `${mode}:${productId}:${from}`;
}

/**
 * Takes the fragment the page shipped with (product pages) into the cache, so
 * coming back to that product after looking at another one stays instant.
 */
function seedFragments(dlg, box) {
    const preloaded = dlg.dataset.sonTrialLoaded;
    if (!preloaded || dlg.dataset.sonTrialSeeded) {
        return;
    }
    dlg.dataset.sonTrialSeeded = 'true';

    const key = fragmentKey(preloaded, '');
    fragments.set(key, box.innerHTML);
    box.dataset.sonTrialShowing = key;
}

function setLoading(dlg, loading) {
    const box = content();
    const spinner = dlg.querySelector('[data-son-trial-loading]');
    if (spinner) {
        spinner.hidden = !loading;
    }
    if (box) {
        box.hidden = loading;
    }
    dlg.toggleAttribute('aria-busy', loading);
}

async function open(productId, trigger) {
    const box = content();
    const dlg = dialog();
    if (!box || !dlg) {
        return false;
    }

    if (!dlg.open) {
        dlg.showModal();
    }

    const mode = trigger.dataset.sonTrialMode || '';
    const key = fragmentKey(productId, trigger.dataset.sonTrialFrom || '', mode);
    seedFragments(dlg, box);

    // which button opened it - the fragment is cached per product and cannot know
    box.dataset.sonTrialEntry = trigger.dataset.sonTrialEntry || '';
    box.dataset.sonTrialMode = mode;

    if (box.dataset.sonTrialShowing !== key) {
        if (!fragments.has(key)) {
            setLoading(dlg, true);
            try {
                const data = await fetchFragment(productId, trigger.dataset.sonTrialFrom || '', mode);
                fragments.set(key, data.html);
            } finally {
                setLoading(dlg, false);
            }
        }
        box.innerHTML = fragments.get(key);
        box.dataset.sonTrialShowing = key;
        dlg.setAttribute('aria-labelledby', 'son-trial-title'); // title exists only once a fragment is in
    }

    const download = box.querySelector('[data-son-trial-download]');
    if (download) {
        selectOs(download, detectOs());
    }
    prefillGuest(box);
    const shown = box.querySelector('[data-son-trial-panel]:not([hidden])') || box;
    shown.querySelector('input:not([type="hidden"]):not([readonly]), [data-son-trial-os].is-active, [data-son-trial]')?.focus();

    return true;
}

async function submit(form) {
    const button = form.querySelector('[type="submit"]');
    if (form.matches('[data-son-trial-form="request"]')) {
        const email = form.elements.email;
        if (!email.value || !email.checkValidity()) {
            showError(form, email.validationMessage || 'Please enter a valid e-mail address.', 'email');
            email.focus();
            return;
        }
        if (form.elements.terms && !form.elements.terms.checked) {
            showError(form, form.elements.terms.validationMessage || 'Please accept the Terms and Conditions and the Privacy Policy.', 'terms');
            return;
        }
    }

    showError(form, '');
    button.disabled = true;
    form.setAttribute('aria-busy', 'true');

    try {
        const fields = Object.fromEntries(new FormData(form));
        const data = await postForm(form.dataset.sonTrialForm, {
            ...fields,
            product_id: Number(fields.product_id),
            terms: 'terms' in fields,
            marketing: 'marketing' in fields,
            entry_point: form.closest('[data-son-trial-content]')?.dataset.sonTrialEntry || '',
        });
        rememberGuest(form);
        showSuccess(form.closest('[data-son-trial-panel]') || content(), data.message);
    } catch (error) {
        showError(form, error.message);
    } finally {
        button.disabled = false;
        form.removeAttribute('aria-busy');
    }
}

document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-son-trial]');
    if (trigger && !trigger.hasAttribute('aria-busy')) {
        event.preventDefault();
        trigger.setAttribute('aria-busy', 'true');
        open(trigger.dataset.sonTrial, trigger)
            .catch(() => {
                dialog()?.close();
                window.location.assign(trigger.href);
            })
            .finally(() => trigger.removeAttribute('aria-busy'));
        return;
    }

    // The installer is served cross-origin as an attachment, so the page does not
    // navigate and this POST finishes on its own. The download is never held up
    // for it, and a lost event costs a tutorial mail, nothing more.
    const get = event.target.closest('[data-son-trial-get]');
    if (get) {
        const mode = content()?.dataset.sonTrialMode || '';
        // a guest on the download page has no address to report
        if (mode && !dialog().dataset.sonTrialNonceEndpoint) {
            return;
        }
        postForm(`${mode ? `${mode}/` : ''}download`, {
            product_id: Number(get.dataset.sonTrialGet),
            os: get.dataset.sonTrialPlatform || '',
        }).catch(() => {});
        return;
    }

    const tab = event.target.closest('[data-son-trial-os]');
    if (tab) {
        selectOs(tab.closest('[data-son-trial-download]'), tab.dataset.sonTrialOs);
        return;
    }

    const view = event.target.closest('[data-son-trial-view]');
    if (view) {
        showView(content(), view.dataset.sonTrialView);
        return;
    }

    const close = event.target.closest('[data-son-trial-close]');
    if (close) {
        close.closest('dialog').close();
        return;
    }

    // a click on the dialog element itself (not its children) is the backdrop
    if (event.target.matches('dialog[data-son-trial-dialog]')) {
        event.target.close();
    }
});

document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-son-trial-form]');
    if (form) {
        event.preventDefault();
        submit(form);
    }
});
